import { useAppSelector } from 'app/config/storeConfig/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { getMapConfig } from '../../model/selectors/getMapConfig';
import { getShapes } from '../../model/selectors/getShapes';
import { deserializeMapConfig, serializeMapConfig } from '../../model/mapConfigSerialization';
import { deserializeShapes } from '../../model/serialization';
import { Shape } from '../../model/types';
import { checkShapesChanges, ShapeChangesResult } from '../helpers/checkShapesChanges';

interface FloorConfigChangesResult {
  shapesChanges: ShapeChangesResult;
  isMapConfigChanged: boolean;
}

export const useWatchFloorConfigChanges = (
  shapesConfig: Nullable<string> | undefined,
  mapConfig: Nullable<string> | undefined,
): FloorConfigChangesResult => {
  const currentShapes = useAppSelector(getShapes);
  const currentMapConfig = useAppSelector(getMapConfig);

  const initialShapes = useRef<Shape[]>([]);
  const initialSerializedMapConfig = useRef('');
  const baselineVersion = useRef(0);

  useEffect(() => {
    initialShapes.current = deserializeShapes(shapesConfig);
    initialSerializedMapConfig.current = serializeMapConfig(deserializeMapConfig(mapConfig));
    baselineVersion.current += 1;
  }, [shapesConfig, mapConfig]);

  return useMemo(
    () => ({
      shapesChanges: checkShapesChanges(initialShapes.current, currentShapes),
      isMapConfigChanged: serializeMapConfig(currentMapConfig) !== initialSerializedMapConfig.current,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- baselineVersion is a ref counter, see comment in useWatchShapeChanges
    [currentShapes, currentMapConfig, baselineVersion.current],
  );
};
